import type { ReactNode } from 'react';
import {
  MarkAbsent,
  MarkCalculated,
  MarkEntered,
  MarkLocked,
  MarkSeal,
  MarkVoid,
} from './marks.js';

/**
 * Measurement — how this product shows a number.
 *
 * Three of the behavioural honesty rules in CLAUDE.md section 8 are really one
 * requirement about numbers, and they are enforced here rather than left to
 * each page to remember:
 *
 *   1. An empty field is never treated as zero.
 *   2. Entered readings must be visually distinct from calculated values.
 *   3. Approved records are locked, and look it.
 *
 * A rule that lives in a document gets followed on the screens somebody
 * remembered it on. A rule that lives in the component that draws every number
 * gets followed everywhere, and breaking it takes deliberate effort.
 *
 * NOTHING HERE KNOWS ANY TEST SCIENCE. No component in this file contains a
 * formula, a unit, a rounding rule, an acceptance limit or a default tolerance.
 * `ReadingRail` draws a tolerance band only when one is passed in from
 * configuration, and draws none otherwise; it will never invent one. That is
 * the CLAUDE.md section 4 gate, expressed as an API that cannot be misused by
 * accident.
 */

export type Provenance = 'entered' | 'calculated';

export interface ReadingProps {
  /**
   * The value, ALREADY FORMATTED for the active locale by `format.ts`.
   *
   * `null` means nothing was recorded. It is rendered as an absence, never as
   * zero — the whole point of accepting `null` here rather than a number is
   * that a caller cannot quietly coalesce a missing reading to `0`.
   */
  readonly value: string | null;
  /** Already translated, e.g. "mm" or "%". */
  readonly unit?: string;
  /** Who produced this number: a person, or the system. */
  readonly provenance?: Provenance;
  /** Already translated. Shown in place of a missing value. */
  readonly absentLabel: string;
  /**
   * Accessible description of the provenance, already translated — for example
   * "entered by the technician" or "calculated by the system". Announced to a
   * screen reader, which cannot see the filled or hollow marker.
   */
  readonly provenanceLabel?: string;
  /** The final result of a test reads one step larger than its inputs. */
  readonly emphasis?: boolean;
}

/**
 * A single measured or derived value.
 *
 * The distinction between the two is drawn the way a plotted chart draws it:
 * a FILLED marker is an observation, a HOLLOW marker is a computed point. That
 * convention is already in the hands of everyone who reads a compaction curve,
 * so the interface borrows it instead of inventing a badge that says
 * "calculated" in words nobody reads twice.
 *
 * The number itself is set in the measurement face at tabular width, so a
 * column of readings aligns on its decimal point rather than merely appearing
 * to.
 */
export function Reading({
  value,
  unit,
  provenance = 'entered',
  absentLabel,
  provenanceLabel,
  emphasis = false,
}: ReadingProps) {
  if (value === null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-default-600">
        <MarkAbsent />
        <span className="text-body italic">{absentLabel}</span>
      </span>
    );
  }

  const isEntered = provenance === 'entered';

  return (
    <span
      className={[
        'inline-flex items-baseline gap-1.5',
        isEntered ? 'text-default-900' : 'text-default-700',
      ].join(' ')}
      {...(provenanceLabel ? { title: provenanceLabel } : {})}
    >
      <span className="inline-flex items-center text-default-600">
        {isEntered ? <MarkEntered size={9} /> : <MarkCalculated size={9} />}
      </span>
      {/*
        The number and its unit are ONE left-to-right isolate (D328), joined by
        a no-break space: under Arabic the unit can neither drift to the other
        side of the number nor wrap away from it.
      */}
      <bdi dir="ltr" className="sci-expr" data-sci="quantity">
        <span
          className={[
            'measure',
            emphasis ? 'text-reading font-semibold' : 'text-body',
            isEntered ? 'font-medium' : 'font-normal',
          ].join(' ')}
        >
          {value}
        </span>
        {unit ? (
          <span className="text-caption text-default-600">
            {'\u00A0'}
            {unit}
          </span>
        ) : null}
      </bdi>
      {provenanceLabel ? <span className="sr-only">{provenanceLabel}</span> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The reading rail
// ---------------------------------------------------------------------------

export interface Tolerance {
  readonly lower: number;
  readonly upper: number;
}

export interface ReadingRailProps {
  readonly min: number;
  readonly max: number;
  /** `null` renders the scale with NO marker: a reading not yet taken. */
  readonly value: number | null;
  /**
   * The acceptance band, when the laboratory has configured one for this test.
   *
   * OMITTED BY DEFAULT AND FOREVER, unless supplied. There is no default band
   * and no inferred band: an acceptance limit is test science, and inventing
   * one is the single thing this project forbids outright (CLAUDE.md section
   * 4). A rail with no tolerance is the normal case, not a degraded one.
   */
  readonly tolerance?: Tolerance;
  /** Already translated, describing the scale for assistive technology. */
  readonly label: string;
  /**
   * Already-formatted end labels, e.g. "0" and "100".
   *
   * `null` is accepted because that is what `format.ts` returns for a value it
   * cannot render, and a caller should not have to launder it into `undefined`
   * — which is exactly the kind of coercion that turns an absent value into a
   * zero somewhere else.
   */
  readonly minLabel?: string | null;
  readonly maxLabel?: string | null;
}

/**
 * A value shown against the scale it was measured on.
 *
 * A bare number tells a reader what was recorded. It does not tell them whether
 * that is near an edge, and "near an edge" is the question a supervisor is
 * actually asking when they scan a result. The rail answers it in one glance
 * without a chart.
 *
 * THE SCALE DOES NOT MIRROR UNDER ARABIC. Everything else in this product
 * flips, and this deliberately does not: an instrument face, a ruler and a
 * plotted axis read low-to-high left-to-right in Arabic technical work exactly
 * as they do in English, because they are drawn objects rather than text. The
 * labels beside it are text and do flip. This is the one place `dir="ltr"` is
 * correct, and it is set explicitly so the next person does not "fix" it.
 */
export function ReadingRail({
  min,
  max,
  value,
  tolerance,
  label,
  minLabel,
  maxLabel,
}: ReadingRailProps) {
  const span = max - min;
  const position = (v: number) =>
    span <= 0 ? 0 : Math.min(100, Math.max(0, ((v - min) / span) * 100));

  const withinTolerance =
    value !== null && tolerance
      ? value >= tolerance.lower && value <= tolerance.upper
      : null;

  return (
    <div className="flex flex-col gap-1">
      <div
        dir="ltr"
        role="img"
        aria-label={label}
        className="relative h-6 w-full select-none"
      >
        {/* The scale itself: a hairline with major ticks at the quarters. */}
        <div className="absolute inset-x-0 top-3 h-px bg-default-400" />
        {[0, 25, 50, 75, 100].map((percent) => (
          <div
            key={percent}
            className="absolute top-3 h-1.5 w-px bg-default-400"
            style={{ left: `${percent}%` }}
          />
        ))}

        {/* The acceptance band, only ever drawn from supplied configuration. */}
        {tolerance ? (
          <div
            className="absolute top-1.5 h-3 border-x border-success-400 bg-success-100"
            style={{
              left: `${position(tolerance.lower)}%`,
              width: `${position(tolerance.upper) - position(tolerance.lower)}%`,
            }}
          />
        ) : null}

        {/*
         * The needle. Absent when nothing was recorded — the scale is drawn and
         * simply carries no reading, which is visibly different from a reading
         * that happens to sit at zero.
         */}
        {value !== null ? (
          <div
            className={[
              'absolute top-0.5 h-5 w-0.5 transition-[left] duration-200 ease-settle',
              withinTolerance === false ? 'bg-danger-500' : 'bg-default-900',
            ].join(' ')}
            style={{ left: `${position(value)}%` }}
          />
        ) : null}
      </div>

      {minLabel || maxLabel ? (
        <div dir="ltr" className="flex justify-between">
          <span className="measure text-micro text-default-600">{minLabel}</span>
          <span className="measure text-micro text-default-600">{maxLabel}</span>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document state
// ---------------------------------------------------------------------------

export type RecordState =
  | 'draft'
  | 'issued'
  | 'superseded'
  | 'cancelled'
  | 'locked';

const SEAL_STYLES: Record<RecordState, string> = {
  draft: 'border-default-400 text-default-600 border-dashed',
  issued: 'border-success-500 text-success-700',
  superseded: 'border-default-500 text-default-600',
  cancelled: 'border-danger-500 text-danger-700',
  locked: 'border-default-700 text-default-800',
};

export interface RecordSealProps {
  readonly state: RecordState;
  /** Already translated. */
  readonly label: string;
  /** Already translated. A number, date or reason shown beneath the label. */
  readonly detail?: string;
}

/**
 * The stamp on a document.
 *
 * A draft must never be mistakable for an issued report, and a superseded copy
 * must never present itself as current (CLAUDE.md section 8). A tinted word in
 * a corner is not enough for that: the seal is a ruled box that occupies real
 * space and reads from across a desk, the way the stamp on the paper version
 * does. A draft's border is dashed, so even a greyscale print of the screen
 * still says draft.
 */
export function RecordSeal({ state, label, detail }: RecordSealProps) {
  const Glyph =
    state === 'issued'
      ? MarkSeal
      : state === 'locked'
        ? MarkLocked
        : state === 'draft'
          ? MarkAbsent
          : MarkVoid;

  return (
    <span
      className={[
        'inline-flex items-center gap-2 border px-2.5 py-1',
        SEAL_STYLES[state],
      ].join(' ')}
    >
      <Glyph size={16} />
      <span className="flex flex-col leading-tight">
        <span className="field-label">{label}</span>
        {detail ? (
          <span className="measure text-micro opacity-80">{detail}</span>
        ) : null}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Authorization
// ---------------------------------------------------------------------------

export interface AuthorizationStepProps {
  /** Already translated: the act, e.g. "Reviewed by". */
  readonly act: string;
  /** The person who performed it, or `null` when it has not happened. */
  readonly actor: string | null;
  /** Already formatted timestamp, or `null`. */
  readonly at: string | null;
  /** Already translated, shown in the empty slot. */
  readonly pendingLabel: string;
}

/**
 * One act in the authorization chain.
 *
 * Review and approval are separate permissions and separate recorded actions,
 * even when the same person performs both (CLAUDE.md section 5.3). The
 * certificate expresses that as separate signature slots, and so does this: an
 * act that has not happened is drawn as an EMPTY RULED SLOT rather than hidden,
 * because the unsigned line is the information. A reader can see at a glance
 * which signatures a record is still missing.
 */
export function AuthorizationStep({
  act,
  actor,
  at,
  pendingLabel,
}: AuthorizationStepProps) {
  const signed = actor !== null;
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="field-label text-default-600">{act}</span>
      <div
        className={[
          'flex min-h-11 flex-col justify-end border-b pb-1',
          signed ? 'border-default-700' : 'border-dashed border-default-400',
        ].join(' ')}
      >
        {signed ? (
          <>
            <span className="truncate text-body font-medium text-default-900">
              {actor}
            </span>
            {at ? (
              <span className="measure text-micro text-default-600">{at}</span>
            ) : null}
          </>
        ) : (
          <span className="text-body italic text-default-600">
            {pendingLabel}
          </span>
        )}
      </div>
    </div>
  );
}

/** The row of authorization slots at the foot of a record. */
export function AuthorizationBlock({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-gutter border-t border-default-300 pt-stack">
      {children}
    </div>
  );
}
